import {
  GridColDef,
  GridEditBooleanCell,
  GridEditBooleanCellProps,
  GridEditInputCell,
  GridPreProcessEditCellProps,
  GridRowModel,
  useGridApiContext,
  useGridApiRef,
} from '@mui/x-data-grid';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';

import DataGrid from 'components/data-grid';
import FlexBox from 'components/flex-box';
import Switch from 'components/switch';
import TextField from 'components/text-field';
import Active from 'views/production-systems/assets/Active';
import Inactive from 'views/production-systems/assets/Inactive';
import LockPondContainer from 'views/production-systems/components/lock-pond';
import PondLockTimer from 'views/production-systems/components/pond-lock-timer';

import { isValidDecimal, isValidMeanWeight } from 'utils/validate-numbers';
import {
  calculateBiomass,
  calculateHarvestQty,
  calculateMeanWeight,
  calculateRemainingQty,
  kgToLbs,
  lbsToKg,
} from 'views/production-systems/utils/farm-overview';

import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { usePondsState } from 'views/production-systems/context/ponds-context';

import { CreateHarvestInput } from 'views/production-systems/features/farm-overview/types/CreateHarvest.model';
import { Harvest } from 'views/production-systems/types/harvest.type';
import { Pond } from 'views/production-systems/types/pond.type';

import { Box, Button, CircularProgress, MobileStepper, useTheme } from '@mui/material';
import DataGridSummary, { DataGridSummaryProps, SummaryItem } from 'components/data-grid/data-grid-summary';
import DataGridToolbar from 'components/data-grid/data-grid-toolbar';
import dayjs from 'dayjs';
import { usePost } from 'hooks/use-post';
import { postHarvest } from 'views/production-systems/api/productionSystems';
import { useDataGridError } from 'views/production-systems/hooks/use-data-grid-error';
import RegistrationHeader from '../../../../components/registration-header';
import ListSelectedPonds from '../list-selected-ponds';
import DuplicateRegistrationWarning, { REGISTRATION_EXISTS_QUERY_KEY } from '../duplicate-registration-warning';
import { RegistrationActionType } from '../../types/registration-action-type.enum';

type Props = {
  ponds: Pond[];
  handleClose?: () => void;
};

type Row = {
  id: number;
  pondName: string;
  currentQty: number;
  currentMw: number;
  currentBm: number;
  harvestQty: number;
  harvestMw: number;
  harvestBm: number;
  isPartialHarvest: boolean;
  remainingQty: number;
  remainingMw: number;
  remainingBm: number;
  pondId: string;
};

const PAGE_NAME_ID = 'productionSystems.farmOverview.harvest';

const AddHarvest: React.FC<Props> = ({ ponds, handleClose }) => {
  const intl = useIntl();
  const apiRef = useGridApiRef();
  const { lockedPonds, globalSiteId, registrationDate } = usePondsState();
  const queryClient = useQueryClient();
  const snackbarDispatch = useSnackbarDispatch();
  const { errorDictionary, changeErrorDictionary, isEditing, handleEditing, hasError } = useDataGridError();

  const theme = useTheme();
  const [confirm, setConfirm] = useState<boolean>(false);
  const [processorCode, setProcessorCode] = useState<string>('');
  const [selectedPonds, setSelectedPonds] = useState<Pond[]>(ponds);
  const [enableKG, setEnableKg] = useState<boolean>(false);

  const initialRows = useMemo(() => {
    return selectedPonds.map((pond) => ({
      id: pond.cycleId,
      pondId: pond.pondId,
      pondName: pond.pondName,
      currentQty: pond.currentQty,
      currentMw: pond.currentMw,
      currentBm: calculateBiomass(pond.currentMw, pond.currentQty, enableKG),
      harvestQty: 0,
      harvestMw: 0,
      harvestBm: 0,
      isPartialHarvest: false,
      remainingQty: 0,
      remainingMw: 0,
      remainingBm: 0,
    }));
  }, [selectedPonds]);

  const [rows, setRows] = useState(initialRows || []);

  useEffect(() => {
    setRows(initialRows);
  }, [initialRows]);

  const calculateRemainingQtyFromFormula = useCallback(
    (remainingBm: number, remainingMw: number) => calculateRemainingQty(remainingBm, remainingMw, enableKG),
    [enableKG]
  );

  const getPartialHarvestDefaults = useCallback(
    (row: Row) => {
      const harvestMw = Number(row.harvestMw) || 0;
      const harvestBm = Number(row.harvestBm) || 0;
      const currentBm = calculateBiomass(Number(row.currentMw) || 0, Number(row.currentQty) || 0, enableKG);

      const remainingMw = harvestMw;
      const remainingBm = Math.max(currentBm - harvestBm, 0);
      const remainingQty = calculateRemainingQtyFromFormula(remainingBm, remainingMw);

      return { remainingMw, remainingBm, remainingQty };
    },
    [calculateRemainingQtyFromFormula, enableKG]
  );

  const processRowUpdate = useCallback(
    (newRow: GridRowModel<Row>) => {
      const previousRow = rows.find((row) => row.pondId === newRow.pondId);
      const defaultPreviousRemainingBm = previousRow
        ? Math.max(calculateBiomass(previousRow.currentMw, previousRow.currentQty, enableKG) - previousRow.harvestBm, 0)
        : 0;

      const wasAutoFilled =
        !!previousRow &&
        previousRow.remainingMw === previousRow.harvestMw &&
        previousRow.remainingBm === defaultPreviousRemainingBm;

      const harvestChanged =
        !!previousRow &&
        (previousRow.harvestMw !== newRow.harvestMw || previousRow.harvestBm !== newRow.harvestBm);

      let updatedRow: Row = {
        ...(newRow as Row),
        harvestQty: calculateHarvestQty(newRow.harvestBm, newRow.harvestMw, enableKG),
      };

      if (!newRow.isPartialHarvest) {
        updatedRow = { ...updatedRow, remainingMw: 0, remainingBm: 0, remainingQty: 0 };
      } else if (!newRow.remainingMw && !newRow.remainingBm && !newRow.remainingQty) {
        updatedRow = { ...updatedRow, ...getPartialHarvestDefaults(newRow as Row) };
      } else if (harvestChanged && wasAutoFilled) {
        // Keep remaining values synced with harvest values while they are still in auto mode.
        updatedRow = { ...updatedRow, ...getPartialHarvestDefaults(newRow as Row) };
      } else {
        updatedRow = {
          ...updatedRow,
          remainingQty: calculateRemainingQtyFromFormula(newRow.remainingBm, newRow.remainingMw),
        };
      }

      setRows((prevRows) => prevRows.map((row) => (row.pondId === newRow.pondId ? updatedRow : row)));

      return updatedRow;
    },
    [enableKG, getPartialHarvestDefaults, calculateRemainingQtyFromFormula, rows]
  );

  //#region Data Grid Components

  function PartialHarvestCheckbox(props: GridEditBooleanCellProps) {
    const { id } = props;
    const apiRef = useGridApiContext();

    const handleValueChange: GridEditBooleanCellProps['onValueChange'] = (event, newValue) => {
      const gridRow = apiRef.current.getRow(id);
      if (!gridRow) return;

      if (newValue) {
        const defaults = getPartialHarvestDefaults(gridRow as Row);

        const updatedRow = { ...gridRow, isPartialHarvest: true, ...defaults };

        setRows((prevRows) => prevRows.map((row) => (row.pondId === gridRow.pondId ? updatedRow : row)));

        return;
      }

      const updatedRow = {
        ...gridRow,
        isPartialHarvest: false,
        remainingBm: 0,
        remainingMw: 0,
        remainingQty: 0,
      };

      setRows((prevRows) =>
        prevRows.map((row) => (row.pondId === gridRow.pondId ? updatedRow : row))
      );
    };

    return <GridEditBooleanCell {...props} onValueChange={handleValueChange} />;
  }

  function renderCheckbox(params: GridEditBooleanCellProps) {
    return <PartialHarvestCheckbox {...params} />;
  }

  //#endregion

  const formatBiomassValue = useCallback(
    (value: number | string | null | undefined) => {
      const numericValue = Number(value);

      if (Number.isNaN(numericValue)) {
        return value;
      }

      return enableKG ? Number(numericValue.toFixed(1)) : Math.round(numericValue);
    },
    [enableKG]
  );

  /** This is to prevent an issue that is caused by pending cell edits when toggling units
   * the values are not updated until the cell edit is committed,
   * so if the user toggles the unit before committing the edit,
   * the values will be converted with the old unit instead of the new unit.
   *
   * stopCellEditMode commits asynchronously, so we read pending edit values directly
   * from the grid's editRows state and merge them into rows synchronously before toggling.
   */
  const flushPendingCellEditsToRows = useCallback(() => {
    const editRowsState = apiRef.current?.state?.editRows as
      | Record<string, Record<string, { value?: unknown }>>
      | undefined;

    if (!editRowsState || Object.keys(editRowsState).length === 0) return;

    setRows((prevRows) =>
      prevRows.map((row) => {
        const pendingFields = editRowsState[row.pondId];
        if (!pendingFields) return row;

        const merged: Row = { ...row };
        Object.entries(pendingFields).forEach(([field, cell]) => {
          const numericValue = Number(cell?.value);
          if (!Number.isNaN(numericValue)) {
            (merged as unknown as Record<string, unknown>)[field] = numericValue;
          }
        });

        return merged;
      })
    );

    Object.entries(editRowsState).forEach(([rowId, fields]) => {
      Object.keys(fields ?? {}).forEach((field) => {
        const cellMode = apiRef.current?.getCellMode(rowId, field);
        if (cellMode === 'edit') {
          apiRef.current?.stopCellEditMode({ id: rowId, field, ignoreModifications: true });
        }
      });
    });
  }, [apiRef]);

  const handleUnitToggle = useCallback(() => {
    flushPendingCellEditsToRows();
    setEnableKg((prev) => !prev);
  }, [flushPendingCellEditsToRows]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'pondName',
        flex: 2,
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.harvestPond` }),
        editable: false,
        renderCell: ({ row }) => {
          return (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                width: '100%',
              }}>
              {!!row.id ? <Active titleAccess="Active" /> : <Inactive titleAccess="Inactive" />}
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={row.pondName}>
                {row.pondName}
              </Box>
            </Box>
          );
        },
      },
      {
        field: 'currentQty',
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.number` }),
        type: 'number',
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        editable: false,
      },
      {
        field: 'currentMw',
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.meanWeight` }),
        type: 'number',
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        editable: false,
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const value = Number(params.props.value);
          const hasError = !isValidMeanWeight(value);
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-newMw`]: hasError });
          return { ...params.props, error: hasError };
        },
      },
      {
        field: 'currentBm',
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.biomass` }, { type: enableKG ? 'kg' : 'lbs' }),
        type: 'number',
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        editable: false,
        valueGetter: (value, row) => calculateBiomass(row.currentMw, row.currentQty, enableKG),
        valueFormatter: (value) => formatBiomassValue(value),
      },
      {
        field: 'harvestQty',
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.harvestNumber` }),
        type: 'number',
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        editable: false,
      },
      {
        field: 'harvestMw',
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.harvestMw` }),
        type: 'number',
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        editable: true,
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const { value } = params.props;
          const hasError = !isValidDecimal(value) || !isValidMeanWeight(Number(value));
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-harvestMw`]: hasError });
          return { ...params.props, error: hasError };
        },
      },
      {
        field: 'harvestBm',
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.harvestBm` }, { type: enableKG ? 'kg' : 'lbs' }),
        type: 'number',
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        editable: true,
        valueFormatter: (value) => {
          const numericValue = Number(value);
          if (Number.isNaN(numericValue)) return value;
          return Math.round(numericValue);
        },
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const { value } = params.props;
          const hasError = !isValidDecimal(value);
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-harvestBm`]: hasError });
          return { ...params.props, error: hasError };
        },
      },
      {
        field: 'isPartialHarvest',
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.partialHarvest` }),
        type: 'boolean',
        flex: 1,
        editable: true,
        renderEditCell: renderCheckbox,
      },
      {
        field: 'remainingQty',
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.remainingNumber` }),
        type: 'number',
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        editable: false,
      },
      {
        field: 'remainingMw',
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.remainingMw` }),
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        type: 'number',
        cellClassName: ({ row }) => {
          return row.isPartialHarvest ? '' : 'cell--disabled';
        },
        editable: true,
        renderEditCell: (params) => <GridEditInputCell {...params} disabled={!params.row.isPartialHarvest} />,
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const { value } = params.props;
          const hasError = !isValidDecimal(value) || !isValidMeanWeight(Number(value));
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-remainingMw`]: hasError });
          return { ...params.props, error: hasError };
        },
      },
      {
        field: 'remainingBm',
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.add.remainingBm` }, { type: enableKG ? 'kg' : 'lbs' }),
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        type: 'number',
        cellClassName: ({ row }) => {
          return row.isPartialHarvest ? '' : 'cell--disabled';
        },
        editable: true,
        valueFormatter: (value) => {
          const numericValue = Number(value);
          if (Number.isNaN(numericValue)) return value;
          return Math.round(numericValue);
        },
        renderEditCell: (params) => <GridEditInputCell {...params} disabled={!params.row.isPartialHarvest} />,
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const { value } = params.props;
          const hasError = !isValidDecimal(value);
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-remainingBm`]: hasError });
          return { ...params.props, error: hasError };
        },
      },
    ],
    [enableKG]
  );

  const summaryValues: SummaryItem[] = useMemo(() => {
    let formValues = rows.reduce(
      (prev, curr, idx) => {
        let biomassKg = calculateBiomass(curr.currentMw, curr.currentQty, true);

        return {
          harvestQty: prev.harvestQty + curr.harvestQty,
          harvestBm: prev.harvestBm + curr.harvestBm,
          remainingQty: prev.remainingQty + curr.remainingQty,
          remainingBm: prev.remainingBm + curr.remainingBm,
          currentBmKg: prev.currentBmKg + biomassKg,
          harvestMwKg: prev.harvestMw + curr.harvestMw,
          remainingMwKg: prev.remainingMw + curr.remainingMw,
          harvestMw: 0,
          remainingMw: 0,
        };
      },
      {
        harvestQty: 0,
        harvestBm: 0,
        remainingQty: 0,
        remainingBm: 0,
        currentBmKg: 0,
        harvestMwKg: 0,
        remainingMwKg: 0,
        harvestMw: 0,
        remainingMw: 0,
      }
    );

    let pondValues = selectedPonds.reduce(
      (prev, curr) => {
        let biomass = calculateBiomass(curr.currentMw, curr.currentQty, enableKG);
        let biomassKg = calculateBiomass(curr.currentMw, curr.currentQty, true);

        return {
          currentQty: curr.currentQty + prev.currentQty,
          currentBm: prev.currentBm + biomass,
          currentBmKg: prev.currentBm + biomassKg,
          remainingBm: prev.currentBm + biomass,
          currentMw: 0,
        };
      },
      {
        currentQty: 0,
        currentMw: 0,
        currentBm: 0,
      }
    );

    let summary = { ...pondValues, ...formValues };

    // finalize some stuff
    summary.currentQty = Math.round(summary.currentQty);
    summary.harvestBm = summary.harvestBm;
    summary.harvestQty = Math.round(summary.harvestQty);
    summary.remainingQty = Math.round(summary.remainingQty);
    summary.currentMw = calculateMeanWeight(summary.currentBmKg, summary.currentQty);
    summary.harvestMw = summary.harvestMwKg;

    summary.remainingMw = summary.remainingMwKg;

    if (!enableKG) {
      summary.currentBm = Math.round(summary.currentBm);
    }

    summary.harvestBm = Math.round(summary.harvestBm);
    summary.remainingBm = Math.round(summary.remainingBm);

    return [
      {
        value: summary.currentQty,
        type: 'number',
      },
      {
        value: summary.currentMw,
        type: 'number',
      },
      {
        value: summary.currentBm,
        type: 'number',
      },
      {
        value: summary.harvestQty,
        type: 'number',
      },
      {
        value: summary.harvestMw,
        type: 'number',
      },
      {
        value: summary.harvestBm,
        type: 'number',
        minimumFractionDigits: 0,
        roundingMode: 'floor',
      },
      {
        value: null,
        hidden: true,
      },
      {
        value: summary.remainingQty,
        type: 'number',
      },
      {
        value: summary.remainingMw,
        type: 'number',
      },
      {
        value: summary.remainingBm,
        type: 'number',
        minimumFractionDigits: 0,
        roundingMode: 'floor',
      },
    ];
  }, [selectedPonds, rows, enableKG]);

  //#endregion FORMS

  //#region HANDLERS

  const handleSubmit = () => {
    let submitRows = rows.map((obj) => ({ ...obj }));
    submitRows.forEach((cycle) => {
      cycle.remainingQty = Math.round(calculateHarvestQty(cycle.remainingBm, cycle.remainingMw, enableKG));
      cycle.harvestQty = Math.round(calculateHarvestQty(cycle.harvestBm, cycle.harvestMw, enableKG));
      cycle.harvestBm = enableKG ? cycle.harvestBm : lbsToKg(cycle.harvestBm);
      cycle.remainingBm = enableKG ? cycle.remainingBm : lbsToKg(cycle.remainingBm);
    });

    let d: CreateHarvestInput = {
      cycles: submitRows,
      date: registrationDate.format('YYYY-MM-DD'),
      processorCode: processorCode,
    };

    createHarvest.mutate({
      globalSiteId,
      data: d,
    });
  };

  //#endregion HANDLERS

  //#region POST DATA

  const createHarvest = usePost<Harvest, { globalSiteId: string; data: CreateHarvestInput }>(
    (request) => postHarvest(request.globalSiteId, request.data),
    (response) => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });

      !!handleClose && handleClose();
      queryClient.invalidateQueries({
        queryKey: ['getPonds']
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  //#endregion POST DATA

  //#region STEPS

  const [activeStep, setActiveStep] = React.useState(0);
  const isCheckingDuplicates = useIsFetching({ queryKey: [REGISTRATION_EXISTS_QUERY_KEY] }) > 0;

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const disableSubmit = useMemo(() => {
    return rows.some((row) => {
      return !isValidDecimal(row.harvestMw);
    });
  }, [rows]);

  useEffect(() => {
    setRows((oldRows) =>
      oldRows.map((newRow) => ({
        ...newRow,
        harvestBm: enableKG ? lbsToKg(newRow.harvestBm) : kgToLbs(newRow.harvestBm),
        remainingBm: enableKG ? lbsToKg(newRow.remainingBm) : kgToLbs(newRow.remainingBm),
      }))
    );
  }, [enableKG]);

  return lockedPonds?.notGranted?.length > 0 && !confirm ? (
    <LockPondContainer handleClose={handleClose} setConfirm={setConfirm} />
  ) : (
    <Box display={'flex'} flexDirection={'column'} gap={4}>
      <PondLockTimer handleDialogClose={handleClose} />
      {activeStep === 0 && (
        <FlexBox>
          <ListSelectedPonds selectedPonds={selectedPonds} setSelectedPonds={setSelectedPonds} />
          <TextField
            label={intl.formatMessage({ id: `${PAGE_NAME_ID}.processorReference` })}
            dark
            fullWidth
            value={processorCode}
            onChange={(e) => setProcessorCode(String(e.target.value))}
          />
        </FlexBox>
      )}
      {activeStep === 1 && (
        <Box display={'flex'} flexDirection={'column'} gap={4}>
          <DuplicateRegistrationWarning actionTypeId={RegistrationActionType.Harvest} ponds={selectedPonds} />
          <RegistrationHeader />

          <DataGrid
            rows={rows}
            apiRef={apiRef}
            disableRowSelectionOnClick
            getRowId={(row) => row.pondId}
            processRowUpdate={processRowUpdate}
            showToolbar
            slots={{
              toolbar: DataGridToolbar,
              footer: DataGridSummary,
            }}
            slotProps={{
              toolbar: {
                csvOptions: {
                  fileName: `${intl.formatMessage({
                    id: 'productionSystems.registrationLog.registrationTypes.harvest',
                  })}-${dayjs().format('DD/MM/YYYY')}`,
                },
                children: (
                  <Box>
                    <Switch
                      dark
                      value={enableKG}
                      onChange={handleUnitToggle}
                      label={intl.formatMessage({ id: `${PAGE_NAME_ID}.add.switchLabel` })}
                    />
                  </Box>
                ),
              },
              footer: {
                summaryValues: summaryValues,
              } as DataGridSummaryProps,
            }}
            getCellClassName={(params) => {
              // if the harvest is read only, it is disabled.
              // if cell has a class name defined (usually to set class if partial harvest, then check that row if partial harvest is enabled. if not, then the cell is disabled)
              // else if the column is not editable, then disable
              // can this be written better? idk if this is the right solution
              let cellClassName = params.colDef.cellClassName;
              if (cellClassName) return params.row.isPartialHarvest ? '' : 'cell--disabled';
              else {
                return !params.colDef.editable ? 'cell--disabled' : '';
              }
            }}
            onCellEditStop={handleEditing}
            onRowClick={({ id }) => {
              const { field } = apiRef.current?.state.tabIndex.cell ?? {};
              apiRef.current?.getCellParams(id, field as string).isEditable && handleEditing();
            }}
            columns={columns}
          />
        </Box>
      )}
      <MobileStepper
        steps={2}
        position="static"
        activeStep={activeStep}
        nextButton={
          activeStep === 1 ? (
            <Button
              disabled={createHarvest.isPending || disableSubmit || isEditing || hasError || isCheckingDuplicates}
              onClick={handleSubmit}
              startIcon={createHarvest.isPending ? <CircularProgress color="inherit" size={18} /> : null}>
              <FormattedMessage id="general.submit" values={{ type: '' }} />
            </Button>
          ) : (
            <Button onClick={handleNext} disabled={activeStep === 1}>
              <FormattedMessage id="general.next" />
            </Button>
          )
        }
        backButton={
          <Button size="small" onClick={handleBack} disabled={activeStep === 0}>
            <FormattedMessage id="general.back" />
          </Button>
        }
      />
    </Box>
  );
};

export default React.memo(AddHarvest);
